import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  ContentCopy as ContentCopyIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Code as CodeIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { ConfirmDialog } from '../components/common';
import copyText from '../utils/copyText';
import EndpointTestDialog from '../components/endpoints/EndpointTestDialog';

const AUTH_OPTIONS = [
  { value: 'api_key', label: 'API key' },
  { value: 'none', label: 'Public' },
];

// Dynamic endpoints are served under /api/e; endpoint.path is stored without that prefix.
const endpointUrl = (endpoint) => `${window.location.origin}/api/e${endpoint.path}`;

const curlCommand = (endpoint) => {
  const parts = [`curl -X ${endpoint.method.toUpperCase()} '${endpointUrl(endpoint)}'`];
  if ((endpoint.auth_type || 'api_key') !== 'none') {
    parts.push("-H 'X-API-Key: <your key>'");
  }
  parts.push("-H 'Content-Type: application/json'", "-d '{}'");
  return parts.join(' ');
};

const EndpointsPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [testTarget, setTestTarget] = useState(null);

  const fetchEndpoints = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/');
      // API returns { endpoints: [...], total, limit, offset }
      const data = response.data;
      if (Array.isArray(data)) {
        setEndpoints(data);
      } else if (data && Array.isArray(data.endpoints)) {
        setEndpoints(data.endpoints);
      } else {
        setEndpoints([]);
      }
    } catch (error) {
      console.error('Error fetching endpoints:', error);
      // Set to empty array on error
      setEndpoints([]);
      if (error.response?.status === 401) {
        enqueueSnackbar('Please log in to access this page', { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to fetch endpoints', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchEndpoints();
  }, [fetchEndpoints]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/endpoints/${id}`);
      enqueueSnackbar('Endpoint deleted successfully', { variant: 'success' });
      fetchEndpoints();
    } catch (error) {
      console.error('Error deleting endpoint:', error);
      if (error.response?.status === 401) {
        enqueueSnackbar('Please log in to perform this action', { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to delete endpoint', { variant: 'error' });
      }
    }
  };

  const handleAuthChange = async (endpoint, authType) => {
    try {
      await api.put(`/endpoints/${endpoint.id}`, { auth_type: authType });
      enqueueSnackbar('Endpoint authentication updated', { variant: 'success' });
      fetchEndpoints();
    } catch (error) {
      console.error('Error updating endpoint auth:', error);
      enqueueSnackbar('Failed to update endpoint authentication', { variant: 'error' });
    }
  };

  const toggleStatus = async (endpoint) => {
    try {
      const newStatus = endpoint.status === 'active' ? 'inactive' : 'active';
      await api.post(`/endpoints/${endpoint.id}/${newStatus === 'active' ? 'enable' : 'disable'}`);
      enqueueSnackbar(`Endpoint ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`, {
        variant: 'success',
      });
      fetchEndpoints();
    } catch (error) {
      console.error('Error toggling endpoint status:', error);
      if (error.response?.status === 401) {
        enqueueSnackbar('Please log in to perform this action', { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to update endpoint status', { variant: 'error' });
      }
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await copyText(text);
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy', { variant: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getMethodColor = (method) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'success';
      case 'POST':
        return 'primary';
      case 'PUT':
      case 'PATCH':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          API Endpoints
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pipelines/new?nodeType=endpoint')}
        >
          Create Endpoint
        </Button>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : endpoints.length === 0 ? (
          <Box textAlign="center" p={4}>
            <Typography variant="h6" gutterBottom>
              No Endpoints Found
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Create your first endpoint to get started.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => navigate('/pipelines/new?nodeType=endpoint')}
              sx={{ mt: 2 }}
            >
              Create Endpoint
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Path</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Auth</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {endpoints.map((endpoint) => (
                  <TableRow key={endpoint.id}>
                    <TableCell>{endpoint.name}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
                        <code>{endpoint.path}</code>
                        <Tooltip title="Copy full URL">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(endpointUrl(endpoint))}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={endpoint.method}
                        color={getMethodColor(endpoint.method)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        variant="standard"
                        disableUnderline
                        value={endpoint.auth_type || 'api_key'}
                        onChange={(e) => handleAuthChange(endpoint, e.target.value)}
                        inputProps={{ 'aria-label': `Authentication for ${endpoint.name}` }}
                        renderValue={(value) => (
                          <Chip
                            label={value === 'none' ? 'Public' : 'API key'}
                            color={value === 'none' ? 'warning' : 'default'}
                            size="small"
                            variant="outlined"
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                        sx={{ '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' } }}
                      >
                        {AUTH_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={endpoint.status}
                        color={getStatusColor(endpoint.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(endpoint.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={endpoint.status === 'active' ? 'Pause' : 'Activate'}>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleStatus(endpoint)}
                          color={endpoint.status === 'active' ? 'warning' : 'success'}
                        >
                          {endpoint.status === 'active' ? <StopIcon /> : <PlayArrowIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={endpoint.pipeline_id ? "Edit Pipeline" : "No linked pipeline"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!endpoint.pipeline_id}
                            onClick={() => {
                              if (endpoint.pipeline_id) {
                                navigate(`/pipelines/${endpoint.pipeline_id}`);
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Test">
                        <IconButton
                          size="small"
                          aria-label={`Test ${endpoint.name}`}
                          onClick={() => setTestTarget(endpoint)}
                        >
                          <ScienceIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="URL and curl">
                        <IconButton size="small" onClick={() => setDetail(endpoint)}>
                          <CodeIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => setDeleteTarget(endpoint)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.name}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="subtitle2">URL</Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <Box component="code" sx={{ flex: 1, wordBreak: 'break-all' }} data-testid="endpoint-url">
                  {endpointUrl(detail)}
                </Box>
                <Tooltip title="Copy URL">
                  <IconButton size="small" aria-label="Copy URL" onClick={() => copyToClipboard(endpointUrl(detail))}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="subtitle2">Authentication</Typography>
              <Typography variant="body2" mb={2}>
                {(detail.auth_type || 'api_key') === 'none' ? 'Public' : 'API key'}
              </Typography>
              <Typography variant="subtitle2">curl</Typography>
              <Box display="flex" alignItems="flex-start">
                <Box component="code" sx={{ flex: 1, wordBreak: 'break-all' }} data-testid="endpoint-curl">
                  {curlCommand(detail)}
                </Box>
                <Tooltip title="Copy curl command">
                  <IconButton size="small" aria-label="Copy curl command" onClick={() => copyToClipboard(curlCommand(detail))}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <EndpointTestDialog
        open={!!testTarget}
        endpoint={testTarget}
        onClose={() => setTestTarget(null)}
        onExampleSaved={(updated) => {
          // Keep the open dialog's endpoint (and its config) current without resetting it.
          setTestTarget((t) => (t && updated && t.id === updated.id ? updated : t));
          fetchEndpoints();
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete endpoint"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => {
          const id = deleteTarget.id;
          setDeleteTarget(null);
          handleDelete(id);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </Container>
  );
};

export default EndpointsPage;
